import { faCircleI, faGrip, faLink, faParagraph, faTrash } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDrag, useDrop } from "react-dnd";
import { TWebsiteBuilderMarqueeItem } from "pages/secure/facility/websiteBuilder/editor/nodes/container/websiteBuilderMarquee";

interface IMarqueeSettingsItemProps {
  marqueeId: string;
  index: number;
  item: TWebsiteBuilderMarqueeItem;
  deleteItem: (itemId: string) => void;
  moveItem: (prevIndex: number, nextIndex: number) => void;
  onClick: () => void;
}

interface IMarqueeSettingsItemDragState {
  isDragging: boolean;
}

interface IMarqueeSettingsItemDropState {
  canDrop: boolean;
  isOver: boolean;
}

interface IMarqueeSettingsDragItem {
  marqueeId: string;
  srcIndex: number;
}

const WEBSITE_BUILDER_MARQUEE_SETTINGS_DROP_ITEM = "website-builder-marquee-settings-item";

export function MarqueeSettingsItem(props: IMarqueeSettingsItemProps) {
  const { marqueeId, index, item, deleteItem, moveItem, onClick } = props;

  const { t } = useTranslation();

  const [{ isDragging }, drag, dragPreview] = useDrag<IMarqueeSettingsDragItem, unknown, IMarqueeSettingsItemDragState>(
    () => ({
      type: WEBSITE_BUILDER_MARQUEE_SETTINGS_DROP_ITEM,
      item: {
        marqueeId,
        srcIndex: index,
      },
      collect: monitor => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [marqueeId, index],
  );

  const [{ canDrop, isOver }, drop] = useDrop<IMarqueeSettingsDragItem, unknown, IMarqueeSettingsItemDropState>(
    () => ({
      accept: WEBSITE_BUILDER_MARQUEE_SETTINGS_DROP_ITEM,
      canDrop: item => item.marqueeId === marqueeId && item.srcIndex !== index,
      drop: item => moveItem(item.srcIndex, index),
      collect: monitor => {
        // Avoid React DnD's "Expected to find a valid target" error when the panel closes on an unrelated drag start.
        const draggingMarqueeItem = monitor.getItemType() === WEBSITE_BUILDER_MARQUEE_SETTINGS_DROP_ITEM;

        return {
          canDrop: draggingMarqueeItem && monitor.canDrop(),
          isOver: draggingMarqueeItem && monitor.isOver(),
        };
      },
    }),
    [marqueeId, index, moveItem],
  );

  function getItemDisplay() {
    if (item.type === "icon") {
      return { icon: faCircleI, text: t("Icon") };
    } else if (item.type === "link") {
      return { icon: faLink, text: item.settings.label };
    } else {
      return { icon: faParagraph, text: item.settings.text };
    }
  }

  const { icon, text } = getItemDisplay();

  return (
    <div
      ref={el => {
        drop(el);
        dragPreview(el);
      }}
      className={classNames("website-builder-marquee-settings-item", {
        "dragging": isDragging,
        "drop-target": canDrop && isOver,
      })}
      onClick={onClick}
    >
      <div
        ref={drag}
        className="marquee-settings-item-grip"
        onClick={e => e.stopPropagation()}
      >
        <FontAwesomeIcon icon={faGrip} />
      </div>
      <div className="marquee-settings-item-content">
        <div className="marquee-settings-item-icon">
          <FontAwesomeIcon icon={icon} />
        </div>
        <div className="marquee-settings-item-text">
          {text}
        </div>
      </div>
      <button
        type="button"
        className="marquee-settings-item-delete"
        onClick={e => {
          e.stopPropagation();
          deleteItem(item.id);
        }}
      >
        <FontAwesomeIcon icon={faTrash} />
      </button>
    </div>
  );
}